import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import {
  deactivateStaff,
  reactivateStaff,
  reissueInvitation,
  setStaffGroup,
  staffQuery,
  type Invitation,
  type Staff,
  type CreatedStaff,
} from '@/api/users';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/Dialog';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { AddColleagueDialog } from './AddColleagueDialog';
import { GROUP_OPTIONS } from './groups';
import { InvitationDialog } from './InvitationDialog';
import { staffName } from './staffName';

interface Props {
  /**
   * The reader's own id, so their row can say *"You"* and carry **no row menu at all** — since
   * BUG-027 all three actions are refused there, so the trigger goes with them (the docblock above
   * says why *New invitation link* joined the other two).
   */
  viewerId: number;
}

type Shown =
  | { kind: 'add' }
  | { kind: 'invitation'; name: string; invitation: Invitation; reissued: boolean }
  | { kind: 'group'; staff: Staff }
  | { kind: 'deactivate'; staff: Staff }
  | { kind: 'reactivate'; staff: Staff };

/**
 * §6.10's **users** half: the directory as a table, with the three writes (backend TASK-099/100).
 *
 * ⚠️ **Every control that does not apply is absent rather than disabled** (§5). The reader's own row
 * carries none of the three — the API refuses each of them with a sentence, and a disabled control
 * would tell them the capability exists and is being withheld. *New invitation link* joined the other
 * two with BUG-027: an invitation *sets a password*, so `issue_invitation` now refuses an account that
 * has one, and a signed-in Admin has a usable password by definition — the item was a **guaranteed**
 * 422 on that one row. With nothing left in it the menu's trigger is absent too, for §6.9's reason: a
 * control that opens nothing has nothing to do.
 *
 * ⚠️ **That is the only row where any of this is predicted, and the reissue guard must not be
 * predicted anywhere else.** `invitation_pending` is *false* for an **expired** invitation on an
 * account whose password is still unusable — precisely the case this route exists for — so a client
 * computing "have they set a password?" from that field would hide the button on the primary use case.
 * What is deliberately **not** hidden either is the last-Admin refusal: it depends on the state of the
 * whole tenant, not of one row, so it is rendered as **the sentence the API returns** when it happens
 * rather than guessed at up front. A client that tried to compute it would need the group counts of
 * every page, and would be wrong the moment another Admin is retired in another tab.
 *
 * ⚠️ **The invitation link appears exactly once, in a dialog, and is never re-displayed.**
 * `InvitationDialog` carries the reasoning; the table shows only *whether* a link is outstanding
 * (`invitation_pending`), which is the whole of what the API will tell anybody afterwards.
 *
 * ⚠️ **Six of this file's strings name a colleague, and all six read one field** — the *Name* cell, the
 * row menu's accessible name, the two confirmation sentences, BUG-027's refusal title and the
 * invitation dialog's *"… has an account."* A blank `name` was therefore not six defects but one, spent
 * six times (BUG-041), so every one of them goes through `staffName` rather than through `row.name`: the
 * floor is in one place, and a site added later inherits it. `staffName`'s docblock carries why a floor
 * exists at all now that the server has one.
 *
 * ⚠️ **This is the only screen that asks for retired accounts, and it asks for them only when a reader
 * ticks *Show retired*** (TASK-116). `visible_staff` is one queryset with two readers — this table and
 * §6.5's interviewer picker — so a default that included retired rows would put a name in the picker
 * that `get_interviewer` answers **404** for. Off is the default and the request then carries no
 * parameter at all. The control needs no permission check of its own: `include_retired` is answered to
 * an `Admin` only, and this whole screen is behind `maySeeSettings`, so a second Admin predicate here
 * could not be false where it is evaluated (BUG-040's rule, and the reason `mayWriteStaff` was
 * deleted). If a reader who may not pass it ever did, the server answers as though they had not — a
 * page of active accounts, which is what this table renders anyway.
 */
export function UsersSection({ viewerId }: Props) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const [shown, setShown] = useState<Shown | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  // §6.9's *Show archived* shape. `queryKeys.staff(query)` already carries the query, so the two
  // answers live in two cache entries without a key change — and the unticked one is keyed and
  // requested exactly as it was before this toggle existed.
  const staff = useQuery(staffQuery(showRetired ? { include_retired: true } : {}));

  /** Every site below names a colleague through this, so none of them can render a blank. */
  const named = (row: Pick<Staff, 'name' | 'email'>) => staffName(row, t('common:unnamedAccount'));

  /**
   * ⚠️ **§8's *empty* state here is *"no colleagues"*, not *"no accounts"*** — and the difference is
   * that this list can never actually be empty: the reader is in it, and a tenant created by
   * `manage.py create_tenant` has exactly one account, theirs. A table showing one row (yourself) with
   * no empty state is a screen that answers *"who works here?"* with a shrug; the state a fresh
   * workspace is really in is *"nobody else yet"*, and its action is the one thing to do about that.
   */
  const rows = staff.data?.items ?? [];
  const alone = rows.length > 0 && rows.every((row) => row.id === viewerId);

  const settle = () => queryClient.invalidateQueries({ queryKey: queryKeyRoots.staff });

  const reissue = useMutation<Invitation, ApiError, Staff>({
    mutationFn: (row) => reissueInvitation(row.id),
    onSuccess: (invitation, row) => {
      settle();
      setShown({ kind: 'invitation', name: named(row), invitation, reissued: true });
    },
  });

  /**
   * What a row's menu offers, decided against **what the API answers for that row** rather than by
   * shape (TASK-116). Three of the four are the same on every row and the fourth is the pair that
   * cannot both apply:
   *
   * * *New invitation link* stays on a retired row, and is the whole point of it being there:
   *   `issue_invitation` deliberately allows a deactivated account that never redeemed — its
   *   password is unusable, so `ACCOUNT_IN_USE` has nothing to refuse — and that is the one request
   *   that gives a reactivated colleague a way in. It is still never *predicted* (the docblock above
   *   says why `invitation_pending` cannot answer it); the refusal is rendered when it comes.
   * * *Change group* stays too: the last-Admin refusal is a fact about the whole tenant, so it is
   *   rendered rather than guessed at, and it is not a certainty here — a retired Admin is not an
   *   **active** one, so `_the_last_admin` does not count them.
   * * ⚠️ ***Retire this account* is absent from a retired row**, and this is the one item decided by
   *   absence: `deactivate_staff` is unconditional, so calling it on an account that is already
   *   retired is a **200 that writes nothing**. That is not a refusal to render — it is a
   *   destructive-looking confirmation for a state the row is already in, which is §6.9's rule about
   *   a control that has nothing to do, one step further on. *Reactivate this account* takes its
   *   slot, so the menu keeps its shape and the row's one real action sits where the reader's eye
   *   already goes.
   */
  const itemsFor = (row: Staff): MenuItem[] => [
    { label: t('users.reissue'), onSelect: () => reissue.mutate(row) },
    { label: t('users.changeGroup'), onSelect: () => setShown({ kind: 'group', staff: row }) },
    row.is_active === false
      ? {
          label: t('users.reactivate'),
          onSelect: () => setShown({ kind: 'reactivate', staff: row }),
        }
      : {
          label: t('users.deactivate'),
          onSelect: () => setShown({ kind: 'deactivate', staff: row }),
          destructive: true,
        },
  ];

  const columns: Array<Column<Staff>> = [
    {
      key: 'name',
      header: t('users.columns.name'),
      primary: true,
      cell: (row) => (
        <span className="flex items-center gap-2">
          {named(row)}
          {row.id === viewerId && <span className={type.metadata}>{t('users.you')}</span>}
        </span>
      ),
    },
    { key: 'email', header: t('users.columns.email'), cell: (row) => row.email },
    {
      key: 'group',
      header: t('users.columns.group'),
      cell: (row) =>
        row.groups.length === 0 ? (
          // A real state of this product — an account exists before its group is set — and one worth
          // seeing: they can be assigned an interview and will not be able to open it.
          <span className={type.metadata}>{t('users.noGroup')}</span>
        ) : (
          row.groups.join(', ')
        ),
    },
    {
      key: 'state',
      header: t('users.columns.state'),
      // ⚠️ **Two different states, and they must not read alike.** *"Has not signed in yet"* is an
      // account waiting for somebody to redeem a live link; *"Retired"* is one that cannot sign in at
      // all. Retirement is checked first because it is the stronger fact — and because
      // `deactivate_staff` revokes the outstanding invitation (BUG-044), so a row that is both is a
      // state the API does not produce; if it ever did, *"Retired"* would still be the true half.
      cell: (row) =>
        row.is_active === false ? (
          <span className={type.metadata} title={t('users.retiredHint')}>
            {t('users.retired')}
          </span>
        ) : row.invitation_pending ? (
          <span className={type.metadata} title={t('users.pendingHint')}>
            {t('users.pending')}
          </span>
        ) : null,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('users.columns.actions')}</span>,
      align: 'right',
      // All three items are refused on the reader's own row, so the row has no menu at all rather
      // than a trigger that opens an empty one (§5, and §6.9's rule about a trigger with nothing
      // behind it). The docblock above says why *New invitation link* is now one of the three.
      cell: (row) =>
        row.id === viewerId ? null : (
          <DropdownMenu
            trigger={
              <Button
                variant="ghost"
                size="sm"
                // §9: this label is the trigger's whole accessible name, so a blank `name` left a
                // control a screen reader cannot announce — the sharpest of BUG-041's seven sites.
                aria-label={t('users.menu', { name: named(row) })}
                className="px-2"
              >
                <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
              </Button>
            }
            items={itemsFor(row)}
          />
        ),
    },
  ];

  return (
    <section>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Eyebrow count={staff.data?.count}>{t('users.eyebrow')}</Eyebrow>
          <p className={cn(type.body, 'mt-2 max-w-[68ch]')}>{t('users.lead')}</p>
        </div>
        {/* One create control on screen at a time — §6.8's rule, paid for there: two ways to make the
            same row is two rows. The empty state below carries it instead when there is nobody else. */}
        {!alone && (
          <Button size="sm" onClick={() => setShown({ kind: 'add' })}>
            <UserPlus size={16} strokeWidth={1.5} aria-hidden />
            {t('users.add')}
          </Button>
        )}
      </div>

      {/*
        ⚠️ **It stays on screen in every state of the table below, the empty one included** — which is
        what keeps *"You are the only account in this workspace."* honest. With the toggle off the
        client holds no retired row and cannot know one exists, so that sentence is the true thing it
        can say about what it has; the one control that would reveal otherwise is right here, above it.
      */}
      <div className="mt-5">
        <Checkbox
          id="users-show-retired"
          label={t('users.showRetired')}
          checked={showRetired}
          onCheckedChange={(checked) => setShowRetired(checked === true)}
        />
      </div>

      {staff.isError ? (
        <ApiErrorState
          error={staff.error}
          message={t('users.error')}
          onRetry={() => staff.refetch()}
        />
      ) : (
        <Table
          className="mt-3"
          columns={columns}
          rows={alone ? [] : rows}
          rowKey={(row) => row.id}
          isLoading={staff.isPending}
          empty={
            <EmptyState
              icon={Users}
              message={t('users.empty')}
              action={
                <Button size="sm" onClick={() => setShown({ kind: 'add' })}>
                  {t('users.add')}
                </Button>
              }
            >
              <p className={type.metadata}>{t('users.emptyHint')}</p>
            </EmptyState>
          }
        />
      )}

      {shown?.kind === 'add' && (
        <AddColleagueDialog
          onClose={() => setShown(null)}
          onCreated={(created: CreatedStaff) =>
            setShown({
              kind: 'invitation',
              // A colleague added with no first or last name and a blank `username` came back with a
              // blank `name`, and the dialog's own sentence then opened with nothing (BUG-041).
              name: named(created.user),
              invitation: created.invitation,
              reissued: false,
            })
          }
        />
      )}

      {shown?.kind === 'invitation' && (
        <InvitationDialog
          name={shown.name}
          invitation={shown.invitation}
          reissued={shown.reissued}
          onClose={() => setShown(null)}
        />
      )}

      {/*
        ⚠️ **The reissue refusal, and it needed a home of its own** (BUG-027). *Change group* and
        *Retire* each own a dialog, so their 422 renders inside the confirmation the reader is already
        looking at; *New invitation link* is a menu item that acts on the click, and the menu is closed
        by the time the answer arrives — so without this the server refused, in four sentences written
        to be read, and the screen did nothing, which reads as a broken button.

        A dialog rather than a line in the row: `ACCOUNT_IN_USE` is four sentences, it names the one
        thing an Admin can do instead (retire the account) and denies what this product has no route
        for (a password reset), and a table cell would either clip that or reflow every row around it.
        The sentence is the dialog's `description`, so §9 gets its `aria-describedby` and the refusal
        is read out with the title rather than sitting in the body unannounced.

        The failure state lives in the mutation — TanStack Query is the one source of truth for it —
        so there is no second copy of it to fall out of step, and dismissing it is `reset()`.
      */}
      {reissue.isError && (
        <Dialog
          open
          onOpenChange={(open) => !open && reissue.reset()}
          /*
           * ⚠️ **The client's own blank, not the server's** (BUG-041): this read
           * `reissue.variables?.name ?? ''`, so the title was *"No new link for "* both for an account
           * the server named blank **and** for a `variables` TypeScript cannot see is always set here.
           * `named` closes the first and the fallback closes the second, rather than manufacturing an
           * empty string to satisfy the interpolation.
           */
          title={t('invitation.refused', {
            name: reissue.variables ? named(reissue.variables) : t('common:unnamedAccount'),
          })}
          // The API's own sentence, exactly as the two dialogs below render theirs.
          description={reissue.error.detail ?? t('invitation.failed')}
          footer={
            <Button onClick={() => reissue.reset()}>
              {t('common:close', { defaultValue: 'Close' })}
            </Button>
          }
        />
      )}

      {shown?.kind === 'group' && (
        <GroupDialog staff={shown.staff} onClose={() => setShown(null)} onDone={settle} />
      )}

      {shown?.kind === 'deactivate' && (
        <DeactivateDialog staff={shown.staff} onClose={() => setShown(null)} onDone={settle} />
      )}

      {shown?.kind === 'reactivate' && (
        <ReactivateDialog staff={shown.staff} onClose={() => setShown(null)} onDone={settle} />
      )}
    </section>
  );
}

/**
 * Move somebody between groups.
 *
 * ⚠️ **The dialog says the change takes effect at once, including for a session already open**, because
 * it does: `require_group` reads the membership on every request, so a demoted colleague loses the
 * screens their old group had without signing out. A reader who assumed otherwise would tell somebody
 * *"you'll see it tomorrow"*.
 */
function GroupDialog({
  staff,
  onClose,
  onDone,
}: {
  staff: Staff;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('settings');
  const [group, setGroup] = useState(staff.groups[0] ?? '');

  const change = useMutation<Staff, ApiError, string>({
    mutationFn: (value) => setStaffGroup(staff.id, value),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('group.title')}
      // Both this sentence and *Retire*'s **open** with the name, so a blank one produced *" is in the
      // Recruiter group."* — the floor is `staffName`'s, not this sentence's (BUG-041).
      description={t('group.description', {
        name: staffName(staff, t('common:unnamedAccount')),
        group: staff.groups.join(', ') || t('users.noGroup'),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={() => change.mutate(group)} disabled={group === '' || change.isPending}>
            {t('group.submit')}
          </Button>
        </>
      }
    >
      <Field label={t('group.label')}>
        {(field) => (
          <Select
            options={GROUP_OPTIONS.map((name) => ({ value: name, label: name }))}
            value={group}
            onValueChange={setGroup}
            {...field}
          />
        )}
      </Field>
      {change.isError && (
        // ⚠️ The API's own sentence: the last-Admin refusal names the action that unblocks the reader
        // ("give somebody else the Admin group first"), which no client-side copy could.
        <p className="mt-3 text-xs text-negative">{change.error.detail ?? t('group.failed')}</p>
      )}
    </Dialog>
  );
}

/**
 * Retire an account.
 *
 * ⚠️ **The confirmation says what is kept, not only what is lost** — the interviews they conducted keep
 * their name and their submitted feedback stays exactly as it is. Without that sentence a reader
 * reasonably fears this erases a colleague's work, and the safe-looking alternative (leaving the
 * account active) is the one that leaves somebody able to sign in after they have left.
 */
function DeactivateDialog({
  staff,
  onClose,
  onDone,
}: {
  staff: Staff;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('settings');

  const retire = useMutation<Staff, ApiError, void>({
    mutationFn: () => deactivateStaff(staff.id),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('deactivate.title')}
      description={t('deactivate.description', {
        name: staffName(staff, t('common:unnamedAccount')),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="danger" onClick={() => retire.mutate()} disabled={retire.isPending}>
            {t('deactivate.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <p className={type.body}>{t('deactivate.kept')}</p>
        {/*
          ⚠️ **This sentence said *"This cannot be undone from here: bringing an account back is an
          operator task."* until TASK-116**, and it was the client's copy of the claim TASK-114
          retracted on the server (`accounts/api.py`, `services.py`, `CONTEXT.md` and the checklist
          all carried a wording of it). It is corrected in place rather than deleted, because what
          replaces it is the part that is still irreversible: reactivation restores the account and
          **not** the link this act revokes (BUG-044).
        */}
        <p className={type.metadata}>{t('deactivate.undo')}</p>
        {retire.isError && (
          <p className="text-xs text-negative">{retire.error.detail ?? t('deactivate.failed')}</p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Bring a retired account back (backend TASK-114).
 *
 * ⚠️ **The confirmation says what reactivation does *not* do, and that is the reason this dialog
 * exists at all rather than the menu item acting on the click.** The act is `is_active = True` and
 * nothing else: the invitation the retirement revoked stays revoked, permanently — so for an account
 * that never set a password, *"welcome back"* is **not** *"they can sign in"*. An Admin who is not
 * told that discovers it through a colleague who cannot get in, which is the shape of BUG-049.
 *
 * ⚠️ **The second sentence is rendered from a fact, never from a guess.** `has_password` says whether
 * the account has a password at all (backend TASK-115); `invitation_pending` cannot stand in for it,
 * because an **expired** invitation leaves that flag false on an account whose password is still
 * unusable — precisely this case. Absent from a payload, the field reads as *no password*, which is
 * the state whose consequence has to be said out loud.
 *
 * ⚠️ **Both branches speak, and the silent one used to be the risky one.** This rendered the sentence
 * for `has_password === false` and **nothing at all** for `true` — so the dialog said what comes back
 * for the account nobody has claimed, and left the account that *does* have a password to be read as
 * *"they keep their own"*. That inference is not one this client can make: a reactivation is two clicks
 * for any Admin from this release, where it used to need an operator in the Django admin, and BUG-044
 * describes the state that reaches it — a reissue overlapping a retirement could leave an account
 * inactive **with a live link**, and whoever held that link set the password now on the row. So the
 * `true` branch says what the field actually means and no more: the account comes back with **whatever
 * password is currently set on it**. That is `resolve_has_password`'s own register on the server
 * (*"It deliberately does not claim the colleague chose it"*), and the wording is deliberately not an
 * accusation — nothing here can tell a claimed account from a taken-over one, and §6.10 has no basis
 * for a warning that says otherwise.
 *
 * ⚠️ **No refusal is predicted here and none is built for.** The route declares `200` and `404` and
 * no `422`: the last-Admin rule can only be moved up by reactivating and the caller's own account is
 * necessarily active, so there is nothing this dialog could warn about in advance. Whatever does come
 * back is rendered as the API's own sentence, exactly as the two dialogs above render theirs.
 */
function ReactivateDialog({
  staff,
  onClose,
  onDone,
}: {
  staff: Staff;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('settings');

  const restore = useMutation<Staff, ApiError, void>({
    mutationFn: () => reactivateStaff(staff.id),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('reactivate.title')}
      // Opens with the name, through the one floor every site here uses (BUG-041).
      description={t('reactivate.description', {
        name: staffName(staff, t('common:unnamedAccount')),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={() => restore.mutate()} disabled={restore.isPending}>
            {t('reactivate.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <p className={type.body}>{t('reactivate.only')}</p>
        <p className={type.body}>
          {staff.has_password ? t('reactivate.hasPassword') : t('reactivate.noPassword')}
        </p>
        {restore.isError && (
          <p className="text-xs text-negative">{restore.error.detail ?? t('reactivate.failed')}</p>
        )}
      </div>
    </Dialog>
  );
}
